import React, { useState, useEffect } from 'react';

import { Badge } from "../../components/invitation/badge";

import { ArrowLeft, Users, UserPlus, FileSpreadsheet, Link, RefreshCw, X, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import InviteModal from "../../components/invitation/InviteModal";
import FunctionManager from "../../components/invitation/FunctionManager";
import { useUserInfo } from "../../hook/useUserInfo";

import { getAllTeamInvitations, resendTeamInvitation, deleteTeamInvitation, inviteBulk } from "../../services/inviteTeamServices";

import { fetchAllGroupApi } from 'src/services/permissionServices';
import { getFunctions } from 'src/services/functions';
import LoadingScreen from 'src/Loading/LoadingScreen';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


interface Tenant {
  id: string;
  name: string;
  contactInfo: string | null;
  image: string | null;
  industry: string | null;
  approved: boolean;
  score: number;
  type: string;
}

interface UserInfo {
  id: string;
  name: string | null;
  email: string;
  username: string;
  title: string | null;
  phone: string | null;
  address: string | null;
  active: boolean;
  lastLogin: string | null;
  isSessionActive: boolean;
  step: string | null;
  groupName: string;
  tenant: Tenant;
}


const TeamOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);


// Adjust the type to match the actual return type of useUserInfo
const { userInfo } = useUserInfo() as { userInfo: Partial<UserInfo> | null };
console.log("User Info:", userInfo);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [invitedMembers, setInvitedMembers] = useState<any[]>([]);
  const [functions, setFunctions] = useState<any[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);



  // Toast notification helper
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    toast[type](message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load groups
        const groupsResponse = await fetchAllGroupApi();
        if (groupsResponse.data) {
          setGroups(groupsResponse.data);
          if (groupsResponse.data.length > 0) {
            setSelectedGroupId(groupsResponse.data[0].id);
          }
        }

        // Load functions
        const functionsResponse = await getFunctions();
        if (functionsResponse.data) {
          setFunctions(functionsResponse.data);
        }

        // Load invitations if tenantId exists
        if (userInfo?.tenant?.id) {
          await loadInvitations();
        }
      } catch (error) {
        showToast("Failed to load data: Could not load required data. Please try again.", 'error');
      } finally {
        setInitialLoading(false);
      }
    };

    loadInitialData();
  }, [userInfo?.tenant?.id]);


  

  const loadInvitations = async () => {
    if (!userInfo?.tenant?.id) {
      showToast("Error: Tenant information not available", 'error');
      return;
    }

    setLoadingInvitations(true);
    try {
      const response = await getAllTeamInvitations(userInfo?.tenant?.id);
      console.log("Invitations response:", response);
      const invitations = response.data || response;
      
      if (Array.isArray(invitations)) {
        setInvitedMembers(invitations.map((invite: any) => ({
          ...invite,
          expiresAt: new Date(invite.expiresAt),
          createdAt: new Date(invite.createdAt)
        })));
      } else {
        throw new Error("Invalid invitations data format");
      }
    } catch (error) {
      console.error("Failed to load invitations:", error);
      showToast("Failed to load invitations: " + (error instanceof Error ? error.message : "Could not load team invitations. Please try again."), 'error');
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleInviteSent = async (newMembers: any[]) => {
    setIsRefreshing(true);
    try {
      setInvitedMembers(prev => [...prev, ...newMembers]);
      await loadInvitations();
      showToast(`Successfully invited ${newMembers.length} team members!`, 'success');
      setInviteModalOpen(false);
    } catch (error) {
      console.error("Failed to refresh invitations:", error);
      showToast("Error: Failed to refresh invitations list", 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleResendInvite = async (invitationId: string) => {
    try {
      const response = await resendTeamInvitation(invitationId);
      if (response.success) {
        showToast("Invitation has been resent successfully!", 'success');
        await loadInvitations();
      } else {
        throw new Error(response.message || "Failed to resend invitation");
      }
    } catch (error) {
      console.error("Resend error:", error);
      showToast("Failed to resend invitation: " + (error instanceof Error ? error.message : "An unexpected error occurred"), 'error');
    }
  };

  const handleRevokeInvite = async (invitationId: string) => {
    try {
      const response = await deleteTeamInvitation(invitationId);
      if (response.success) {
        showToast("Invitation has been revoked successfully!", 'success');
        setInvitedMembers(prev => prev.filter(m => m.id !== invitationId));
      } else {
        throw new Error(response.message || "Failed to revoke invitation");
      }
    } catch (error) {
      console.error("Revoke error:", error);
      showToast("Failed to revoke invitation: " + (error instanceof Error ? error.message : "An unexpected error occurred"), 'error');
    }
  };

  const handleBulkUpload = async (csvData: any[]) => {
    if (!userInfo?.tenant?.id) {
      showToast("Please ensure tenant is available", 'error');
      return;
    }

    try {
      const invitations = csvData.map((row, idx) => {
        const group = groups.find(g => g.name.trim().toLowerCase() === (row.groupName || '').trim().toLowerCase());
        if (!group) throw new Error(`Row ${idx + 2}: Group '${row.groupName}' not found.`);
        const functionNames = (row.functionNames || '').split(',').map((fn: string) => fn.trim()).filter(Boolean);
        const functionIds = functionNames.map((fnName: string) => {
          const func = functions.find((f: any) => f.name.trim().toLowerCase() === fnName.toLowerCase());
          if (!func) throw new Error(`Row ${idx + 2}: Function '${fnName}' not found.`);
          return func.id;
        });
        return {
          email: row.email,
          fullName: row.fullName || row.name,
          groupId: group.id,
          functionIds,
          tenantId: userInfo.tenant!.id
        };
      });

      const response = await inviteBulk(invitations);
      
      if (response.success) {
        showToast(`Successfully sent ${invitations.length} invitations!`, 'success');
        await loadInvitations();
      } else {
        throw new Error(response.message || "Failed to send bulk invitations");
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      showToast("Failed to send bulk invitations: " + (error instanceof Error ? error.message : "An unexpected error occurred"), 'error');
    }
  };

  const downloadCSVTemplate = () => {
    const groupNames = groups.map(g => g.name).join(', ');
    const functionNames = functions.map((f: any) => f.name).join(', ');
    const instructions = [
      '# Please fill in the following columns.',
      '# Use the exact group and function names as shown below.',
      '# Multiple function names should be separated by commas.',
      `# Available group names: ${groupNames}`,
      `# Available function names: ${functionNames}`,
    ];
    const headers = ['email', 'fullName', 'groupName', 'functionNames'];
    const sampleData = [
      ['john.doe@example.com', 'John Doe', groups[0]?.name || '', functions.slice(0,2).map((f: any) => f.name).join(', ')],
      ['jane.smith@example.com', 'Jane Smith', groups[1]?.name || '', functions[0]?.name || ''],
    ];
    
    const csvContent = [
      ...instructions,
      headers.join(','),
      ...sampleData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'team_invitations_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    showToast("CSV template has been downloaded!", 'info');
  };


  const getRoleColor = (role: string) => {
    // Map role names to colors based on common patterns
    const roleColors: { [key: string]: string } = {
      'manager': 'bg-green-100 text-green-800',
      'admin': 'bg-red-100 text-red-800',
      'auditor': 'bg-purple-100 text-purple-800',
      'implementor': 'bg-blue-100 text-blue-800',
      'member': 'bg-gray-100 text-gray-800'
    };
    return roleColors[role] || 'bg-gray-100 text-gray-800';
  };

  const getFunctionNames = (member: any) => {
    return member.functionIds?.map((id: string) => {
      const func = functions.find(f => f.id === id);
      return func ? func.name : `Function ${id}`;
    }) || [];
  };

  const getStatusBadge = (member: any) => {
    const now = new Date();
    const isExpired = now > new Date(member.expiresAt);
    
    if (member.status === 'accepted') {
      return <Badge className="bg-green-100 text-green-800">Accepted</Badge>;
    }
    
    if (isExpired) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    
    return <Badge className="bg-blue-100 text-blue-800">Pending</Badge>;
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      {initialLoading && (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(255,255,255,0.8)' }}>
       <LoadingScreen/>
        </div>
      )}
   
     
      <div className="container px-4 root  "style={{ marginTop:"100px" }}>
        <div className="mx-auto" style={{ maxWidth: 1200 }}>
         
      <div className="text-center">
  <h1 className="display-6 fw-bold text-primary mb-3">Add your team members</h1>
  <p className="lead text-secondary mb-4">
    Invite colleagues to collaborate on control assessment and evidence collection
  </p>
 
</div>
          <FunctionManager onGroupSelect={setSelectedGroupId} />
          <div className="bg-white rounded mb-4"  style={{ 
    borderRadius: "10px",
    border: "1px solid #e5e7eb", // Tailwind's gray-200
    background: "#fff",
  
    overflow: "hidden" // Ensures border-radius clips child elements
  }}>
            <div className="px-4 py-3 d-flex align-items-center">
              <div className="h5 text-primary fw-semibold d-flex align-items-center mb-0">
                <Users className="me-2" size={20} /> Invite Team Members
              </div>
            </div>
            <div className="px-4 py-4">
              <div className="row g-3">
                <div className="col-md-4">
                  <button
                    onClick={() => setInviteModalOpen(true)}
                    className=" btndoweload text-white w-100 h-100 
                    d-flex flex-column align-items-center justify-content-center"
                    type="button"
                  >
                    <UserPlus className="mb-1" size={20} />
                    <span>Individual Invites</span>
                  </button>
                </div>
                <div className="col-md-4">
                  <button
                    onClick={downloadCSVTemplate}
                    className="btn-upload w-100 h-100 d-flex flex-column
                     align-items-center justify-content-center"
                    type="button"
                  >
                    <FileSpreadsheet className="mb-1" size={20} />
                    <span>Bulk Upload CSV</span>
                  </button>
                </div>
                <div className="col-md-4">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('https://eu.portal.com/invite/abc123');
                      window.alert("Invite URL copied! Share this link with your colleagues.");
                    }}
                    className="btn btn-outline-success w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                    type="button"
                  >
                    <Link className="mb-1" size={20} />
                    <span>Copy Invite URL</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Invitations Table */}
          {isRefreshing ? (
            <div className="d-flex justify-content-center py-5">
              <Loader2 className="spinner-border text-primary" style={{ width: 32, height: 32 }} />
            </div>
          ) : loadingInvitations ? (
            <div className="d-flex justify-content-center ">
              <Loader2 className="spinner-border text-primary" style={{ width: 32, height: 32 }} />
            </div>
          ) : invitedMembers.length > 0 ? (
            <div className="bg-white  rounded mb-4">
              <div className="px-4 py-3 border-bottom">
                <div className="h5 text-primary fw-semibold mb-0">Team Invitations ({invitedMembers.length})</div>
              </div>
              <div className="px-4 py-4 ">
                <table className="table align-middle">
                  <thead className="">
                    <tr>
                      <th>Member</th>
                      <th>Role</th>
                      <th>Functions</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitedMembers.map((member) => {
                      const now = new Date();
                      const isExpired = now > new Date(member.expiresAt);
                      return (
                        <tr key={member.id}>
                          <td>
                            <div>
                              <div className="fw-medium text-dark">{member.fullName || member.name || 'No name'}</div>
                              <div className="small text-secondary">{member.email}</div>
                            </div>
                          </td>
                          <td>
                            <span >{member.group?.name || 'member'}</span>
                          </td>
                          <td>
                            <div>
                              {(member.functions || []).length > 0
                                ? member.functions.map((func: any, index: number) => (
                                    <div key={index} className="small fw-medium">
                                      {func.name}
                                    </div>
                                  ))
                                : <span className="text-muted small">No functions</span>
                              }
                            </div>
                          </td>
                          <td>
                            {member.status === 'accepted' ? (
                              <span className="badge bg-success">Accepted</span>
                            ) : (new Date() > new Date(member.expiresAt)) ? (
                              <span className="badge bg-danger">Expired</span>
                            ) : (
                              <span className="badge bg-primary">Pending</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                            
                                <button
                                  type="button"
                                  onClick={() => handleResendInvite(member.id)}
                                  className="btn btn-outline-success btn-sm"
                                >
                                  <RefreshCw className="me-1" size={14} />
                                  Resend
                                </button>
                            
                              <button
                                type="button"
                                onClick={() => handleRevokeInvite(member.id)}
                                className="btn btn-outline-danger btn-sm"
                              >
                                <X className="me-1" size={14} />
                                Revoke
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="py-5 text-center text-muted">
              No invitations yet. Invite team members to see them here.
            </div>
          )}
        
          {inviteModalOpen&&userInfo?.tenant?.id && !initialLoading && (
            <div className="modal-backdrop" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1040,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div className="modal-content" style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                maxWidth: '800px',
                width: '90%'
              }}>
                <InviteModal
                  open={inviteModalOpen}
                  onOpenChange={setInviteModalOpen}
                  onInvitesSent={handleInviteSent}
                  tenantId={userInfo?.tenant?.id || ''}
                  groupId={selectedGroupId}
                  functions={functions}
                  groups={groups}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamOnboarding;
